/**
 * Money is ALWAYS integer micro-units internally. 1 USD = 1_000_000 micros.
 * Never use floating point for accounting. External APIs serialise as fixed
 * 6-decimal strings ("0.001200").
 */
export const MICROS_PER_UNIT = 1_000_000;
export type Micros = number; // always a safe integer

export function assertMicros(v: number, label = "amount"): Micros {
  if (!Number.isSafeInteger(v)) throw new RangeError(`${label} must be a safe integer of micros, got ${v}`);
  return v;
}

const DECIMAL_RE = /^(-)?(\d{1,12})(?:\.(\d{1,6}))?$/;

/** Parse "0.0012" / "12" / "-3.5" into micros exactly (no float math). */
export function parseDecimalToMicros(s: string): Micros {
  const m = DECIMAL_RE.exec(s.trim());
  if (!m) throw new RangeError(`invalid decimal money string: ${s}`);
  const whole = Number(m[2]);
  const frac = Number((m[3] ?? "").padEnd(6, "0"));
  const v = whole * MICROS_PER_UNIT + frac;
  return assertMicros(m[1] ? -v : v);
}

export function formatMicros(v: Micros, decimals = 6): string {
  assertMicros(v);
  const neg = v < 0;
  const abs = Math.abs(v);
  const whole = Math.floor(abs / MICROS_PER_UNIT);
  const frac = String(abs % MICROS_PER_UNIT).padStart(6, "0").slice(0, decimals);
  return `${neg ? "-" : ""}${whole}${decimals > 0 ? "." + frac : ""}`;
}

/** Basis points share with banker-safe floor; remainder stays with the platform side explicitly. */
export function applyBps(amount: Micros, bps: number): Micros {
  assertMicros(amount);
  if (!Number.isInteger(bps) || bps < 0 || bps > 10_000) throw new RangeError(`bps out of range: ${bps}`);
  return Math.floor((amount * bps) / 10_000);
}

/**
 * Split a consumer charge into marketplace fee and provider gross.
 * Invariant: fee + provider === price (no micro lost or created).
 */
export function splitCharge(price: Micros, marketplaceFeeBps: number): { fee: Micros; provider: Micros } {
  const provider = price - applyBps(price, marketplaceFeeBps);
  return { fee: price - provider, provider };
}
